import { useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Copy, AlertTriangle } from "lucide-react";

export const ResetPasswordDialog = ({ open, onOpenChange, usuario }) => {
  const [temporal, setTemporal] = useState("");
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cerrar = (v) => {
    if (!v) { setTemporal(""); setResultado(null); }
    onOpenChange(v);
  };

  const resetear = async () => {
    setCargando(true);
    try {
      const { data } = await api.post(`/usuarios/${usuario.id}/resetear-password`,
        { password_temporal: temporal.trim() || null });
      setResultado(data);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(resultado.password_temporal);
      toast.success("Contraseña temporal copiada");
    } catch {
      toast.error("No se pudo copiar; anótela manualmente");
    }
  };

  if (!usuario) return null;
  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="bg-white max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} /> Resetear contraseña — {usuario.usuario}
          </DialogTitle>
        </DialogHeader>
        {!resultado ? (
          <div className="space-y-3">
            {usuario.password_reset && (
              <p className="text-xs text-muted-foreground rounded-sm bg-secondary px-3 py-2">
                Último reset: por <strong>{usuario.password_reset.por}</strong> el{" "}
                {new Date(usuario.password_reset.fecha).toLocaleString("es-CR")}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="pw-temporal">Contraseña temporal (opcional)</Label>
              <Input id="pw-temporal" data-testid="reset-password-input" value={temporal}
                onChange={(e) => setTemporal(e.target.value)} autoComplete="off"
                placeholder="Dejar vacío para generar una automáticamente" />
              <p className="text-xs text-muted-foreground">
                El usuario deberá definir una contraseña nueva en su próximo ingreso.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => cerrar(false)}>Cancelar</Button>
              <Button data-testid="reset-password-confirmar-btn" onClick={resetear}
                disabled={cargando || (temporal.trim() !== "" && temporal.trim().length < 6)}>
                {cargando ? "Reseteando..." : "Resetear contraseña"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reset-password-resultado">
            <p className="text-sm text-foreground">
              Contraseña temporal de <strong>{resultado.usuario}</strong>:
            </p>
            <div className="flex items-center gap-2">
              <code data-testid="reset-password-temporal"
                className="flex-1 rounded-sm border border-border bg-secondary px-3 py-2 font-mono text-base tracking-wide select-all">
                {resultado.password_temporal}
              </code>
              <Button variant="outline" size="icon" onClick={copiar} data-testid="reset-password-copiar-btn" title="Copiar">
                <Copy size={15} />
              </Button>
            </div>
            <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Se muestra <strong>una sola vez</strong>. Entréguela al usuario: al ingresar con ella,
              el sistema le exigirá definir su propia contraseña antes de continuar.</span>
            </div>
            <div className="flex justify-end">
              <Button data-testid="reset-password-cerrar-btn" onClick={() => cerrar(false)}>Entendido</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
